import { useCaseQueue } from "src/cases/queries/queues";
import {
  CaseTemplateQueryResults,
  useCaseTemplatesByIds,
} from "src/cases/queries/templates";
import {
  CaseQueueColumnSchemas,
  DeserializedCaseTemplateVersion,
} from "src/cases/queues/types";
import { extractQueueColumnSchemas } from "src/cases/queues/utils";
import { CasePropertiesValue, EnumValue } from "src/clients/case-management";

type UseQueueColumnSchemasResult = {
  columnSchemas: CaseQueueColumnSchemas;
  statusValues: EnumValue[];
  isLoading: boolean;
};

const extractStatusEnumValues = (
  versions: DeserializedCaseTemplateVersion[],
): EnumValue[] =>
  versions.flatMap((version) => {
    const statusSchema = version.case_properties.status as
      | CasePropertiesValue
      | undefined;
    return statusSchema?.type === "enum" ? (statusSchema.values ?? []) : [];
  });

const combineToColumnSchemas = (
  results: CaseTemplateQueryResults,
): UseQueueColumnSchemasResult => {
  const versions = results.flatMap((r) => r.data?.versions ?? []);

  return {
    columnSchemas: extractQueueColumnSchemas(versions),
    statusValues: extractStatusEnumValues(versions),
    isLoading: results.some((r) => r.isLoading),
  };
};

export const useQueueColumnSchemas = (
  queueId: string | undefined,
): UseQueueColumnSchemasResult => {
  const { data: caseQueue, isLoading: isLoadingQueue } = useCaseQueue(queueId);

  const templateIds =
    caseQueue?.template_configs?.map((config) => config.template.id) ?? [];

  const {
    columnSchemas,
    // Use separate value to avoid refactoring of the old filters
    // Remove this and integrate to columnSchemas when we drop support for old filters
    // Ticket: RES-4789
    statusValues,
    isLoading: isLoadingTemplates,
  } = useCaseTemplatesByIds(templateIds, { combine: combineToColumnSchemas });

  return {
    columnSchemas,
    statusValues,
    isLoading: isLoadingQueue || isLoadingTemplates,
  };
};
